<script lang="ts">
  /**
   * MoveToProjectPicker - a free-floating MoveToProjectPanel.
   *
   * Task menus now drill into MoveToProjectPanel in place; this wrapper remains for
   * flows that aren't inside a menu (e.g. "move incomplete tasks" in ProjectRail and
   * the project page).
   *
   * Uses fixed positioning via Floating UI to avoid clipping by overflow:hidden parent containers.
   */
  import { tick } from 'svelte';
  import { computePosition, flip, shift, offset, autoUpdate } from '@floating-ui/dom';
  import MoveToProjectPanel from './MoveToProjectPanel.svelte';

  interface Props {
    /** The project ID to exclude (the current project) */
    excludeProjectId: string;
    /** The button/element that triggered this picker — used for positioning */
    anchorEl: HTMLElement;
    /** Called when a project is selected */
    onSelect: (projectId: string, projectTitle: string) => void;
    /** Called when the picker is closed/cancelled */
    onClose: () => void;
  }

  let { excludeProjectId, anchorEl, onSelect, onClose }: Props = $props();

  let pickerEl: HTMLDivElement | undefined = $state();
  let panel: MoveToProjectPanel | undefined = $state();
  let focused = false;
  let floatingStyle = $state('position: fixed; left: 0; top: 0; visibility: hidden;');

  // Position the picker using Floating UI and keep it anchored on scroll/resize
  // eslint-disable-next-line momentium/no-unguarded-effect -- Floating UI positioning lifecycle with cleanup
  $effect(() => {
    if (!pickerEl || !anchorEl) return;
    return autoUpdate(anchorEl, pickerEl, updatePosition);
  });

  // Close on click outside
  // eslint-disable-next-line momentium/no-unguarded-effect -- document event listener lifecycle
  $effect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (pickerEl && !pickerEl.contains(e.target as Node)) {
        onClose();
      }
    }
    // Use setTimeout to avoid catching the click that opened this picker
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  async function updatePosition() {
    if (!pickerEl || !anchorEl) return;
    const result = await computePosition(anchorEl, pickerEl, {
      strategy: 'fixed',
      placement: 'right-start',
      middleware: [
        offset(4),
        flip({
          fallbackPlacements: ['left-start', 'bottom-start', 'top-start'],
        }),
        shift({ padding: 8 }),
      ],
    });
    floatingStyle = `position: fixed; left: ${result.x}px; top: ${result.y}px; visibility: visible; z-index: 200;`;
    // Focus once the picker is visible and in its final spot.
    if (!focused) {
      focused = true;
      await tick();
      panel?.focus();
    }
  }
</script>

<div class="picker-container" bind:this={pickerEl} style={floatingStyle}>
  <MoveToProjectPanel bind:this={panel} {excludeProjectId} {onSelect} onEscape={onClose} />
</div>

<style>
  .picker-container {
    width: 280px;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    box-shadow:
      0 10px 25px rgba(0, 0, 0, 0.15),
      0 4px 10px rgba(0, 0, 0, 0.08);
    overflow: hidden;
  }
</style>
